import React, { useEffect, useMemo, useRef, useState } from 'react';
import { roundTo, toNumber } from '../../../utils/billToOrderCalc';
import { catalogItemsForTool } from '../../../utils/billToItemCatalog';
import { aggregateWeeklyItemHistory } from '../../../utils/billToDailySales';
import {
  buildSkuPlanningRows,
  closingStockRowsFromWizard,
  itemSkuKey,
} from '../../../utils/billToSkuPlanning';
import {
  FormulaInfoHint,
  OVERALL_PROJECTED_FORMULAS,
  SKU_PLANNING_FORMULAS,
} from './FormulaInfoHint';
import SkuPlanningTable from './SkuPlanningTable';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const EMPTY_PARAMS = {};

function sanitizeNumeric(raw, allowNegative) {
  const value = String(raw ?? '');
  const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  if (value === '' || pattern.test(value)) return value;
  return null;
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col text-sm text-gray-700">
      <span>
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function asParam(value, digits) {
  return String(roundTo(value, digits));
}

function StepSetParameters({ wizardData, onChange }) {
  const params = wizardData.parameters || EMPTY_PARAMS;
  const [skuSearch, setSkuSearch] = useState('');
  const prevProjectedRef = useRef({ sales: null, gc: null });

  const catalogTool = useMemo(
    () => ({
      toolType: wizardData.toolType,
      categories: wizardData.categories,
      subCategories: wizardData.subCategories,
      selectedItemKeys: wizardData.selectedItemKeys,
      productCatalog: wizardData.productCatalog,
    }),
    [
      wizardData.toolType,
      wizardData.categories,
      wizardData.subCategories,
      wizardData.selectedItemKeys,
      wizardData.productCatalog,
    ]
  );
  const selectedProducts = useMemo(() => catalogItemsForTool(catalogTool), [catalogTool]);

  const closingStockRows = useMemo(
    () => closingStockRowsFromWizard(wizardData),
    [wizardData]
  );

  const weekCount = Math.max(3, toNumber(params.salesHistoryWeeks, 3));
  const weeklyHistory = useMemo(
    () => aggregateWeeklyItemHistory(wizardData.dailySalesRows || [], weekCount),
    [wizardData.dailySalesRows, weekCount]
  );

  const skuPlanningRows = useMemo(
    () =>
      buildSkuPlanningRows({
        items: selectedProducts,
        wizardData,
        weeklyHistory,
        weekCount,
        closingStockRows,
      }),
    [selectedProducts, weeklyHistory, weekCount, wizardData, closingStockRows]
  );

  const overallProjected = useMemo(() => {
    let sales = 0;
    let gc = 0;
    let count = 0;
    skuPlanningRows.forEach((row) => {
      if (!row.hasHistory) return;
      sales += toNumber(row.projectedSales);
      gc += toNumber(row.projectedGc);
      count += 1;
    });
    return {
      sales: asParam(sales, 0),
      gc: asParam(gc, 0),
      skuCount: count,
    };
  }, [skuPlanningRows]);

  const derivedProjectedSales = overallProjected.sales;
  const derivedProjectedGc = overallProjected.gc;

  useEffect(() => {
    const next = { sales: derivedProjectedSales, gc: derivedProjectedGc };
    const prev = prevProjectedRef.current;
    const sourceChanged = prev.sales !== next.sales || prev.gc !== next.gc;
    prevProjectedRef.current = next;
    if (!sourceChanged && prev.sales != null) return;
    onChange({
      parameters: {
        projectedSales: next.sales,
        projectedGc: next.gc,
      },
    });
  }, [derivedProjectedSales, derivedProjectedGc, onChange]);

  const visibleProducts = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return skuPlanningRows;
    return skuPlanningRows.filter(({ item }) =>
      [item.itemName, item.itemCode, item.subCategory, item.category]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [skuPlanningRows, skuSearch]);

  const updateParam = (key, value) => {
    onChange({ parameters: { ...params, [key]: value } });
  };

  const updateFinalOrderByOutlet = (item, value) => {
    const key = itemSkuKey(item);
    onChange({
      planningBySku: {
        ...(wizardData.planningBySku || {}),
        [key]: {
          ...(wizardData.planningBySku?.[key] || {}),
          finalOrderByOutlet: value,
          finalOrderByOutletEdited: true,
        },
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Step 3: Set Parameters</h3>
        <p className="mt-1 text-sm text-gray-500">
          Review lead time, buffer %, and projected growth from Configure Tool (edit there if needed).
          Projected sales and GC use Actual GC &amp; Sales from Configure Tool. Then complete the tool
          details.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">SKU Planning</h4>
            <p className="my-2 text-xs text-gray-500">
              Checked SKUs from Configure Tool. Planning values are read-only here; use the edit icon on
              Step 2 to change them.
            </p>
            <FormulaInfoHint title="SKU Planning — formulas" items={SKU_PLANNING_FORMULAS} />
          </div>
          <input
            type="text"
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            placeholder="Search SKU NAME or Id"
            className="w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:max-w-xs"
          />
        </div>
        {selectedProducts.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            Select SKUs in Configure Tool to set parameters.
          </p>
        ) : (
          <SkuPlanningTable
            rows={visibleProducts}
            wizardData={wizardData}
            allowEditOutlet
            onOutletOrderChange={updateFinalOrderByOutlet}
          />
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h4 className="mb-1 text-sm font-semibold text-gray-800">Overall Projected Sales / GC</h4>
        <p className="mb-2 text-xs text-gray-500">
          Totals for the {selectedProducts.length} selected SKU
          {selectedProducts.length === 1 ? '' : 's'} carried from Configure Tool (sum of per-SKU
          projected values). You can edit both overall values.
        </p>
        <FormulaInfoHint
          title="Overall Projected Sales / GC — formulas"
          items={OVERALL_PROJECTED_FORMULAS}
        />
        <div className="mt-2 grid gap-4 sm:grid-cols-2">
          <Field label="Projected Sales" required>
            <input
              type="text"
              inputMode="decimal"
              value={params.projectedSales ?? ''}
              onChange={(e) => {
                const next = sanitizeNumeric(e.target.value, false);
                if (next != null) updateParam('projectedSales', next);
              }}
              placeholder="e.g. 120000"
              className={inputClasses}
            />
          </Field>
          <Field label="Projected GC" required>
            <input
              type="text"
              inputMode="decimal"
              value={params.projectedGc ?? ''}
              onChange={(e) => {
                const next = sanitizeNumeric(e.target.value, false);
                if (next != null) updateParam('projectedGc', next);
              }}
              placeholder="e.g. 850"
              className={inputClasses}
            />
          </Field>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="mb-4 text-sm font-semibold text-gray-800">Tool Details</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col text-sm text-gray-700">
            <span>
              Tool Name <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              value={wizardData.toolName || ''}
              onChange={(e) => onChange({ toolName: e.target.value })}
              placeholder="Build To - Ordering Tool Showroom Items"
              className={inputClasses}
            />
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            Tool Code
            <input
              type="text"
              value={wizardData.toolCode || ''}
              onChange={(e) => onChange({ toolCode: e.target.value })}
              placeholder="Auto-generated if left blank"
              className={inputClasses}
            />
          </label>
          <label className="flex flex-col text-sm text-gray-700 sm:col-span-2">
            Description (Optional)
            <textarea
              value={wizardData.toolDescription || ''}
              onChange={(e) => onChange({ toolDescription: e.target.value })}
              placeholder="Weekly ordering tool for showroom items."
              rows={3}
              className={inputClasses}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

export default StepSetParameters;
